/**
 * Froala Editor Paragraph Format Extended plugin v0.2.0 (https://github.com/Finesse/froala-editor-paragraph-format-extended-plugin)
 * Copyright 2016-2019 Surgie Finesse
 * Licensed under the MIT license
 */

import FroalaEditor from 'froala-editor';

FroalaEditor.DEFAULTS = {
  ...FroalaEditor.DEFAULTS,
  paragraphFormatExtended: [
    { title: 'Normal' },
    { tag: 'h1', title: 'Heading 1' },
    { tag: 'h2', title: 'Heading 2' },
    { tag: 'h3', title: 'Heading 3' },
    { tag: 'h4', title: 'Heading 4' },
    { tag: 'h4', class: 'fr-text-bordered', title: 'Header 4 bordered' },
    { tag: 'pre', title: 'Code' },
  ],
  paragraphFormatExtendedSelection: false,
  pasteDeniedAttrs: FroalaEditor.DEFAULTS.pasteDeniedAttrs.filter((attr) => attr !== 'class'),
};

FroalaEditor.PLUGINS.paragraphFormatExtended = function (editor: any) {
  const $ = editor.$;

  function apply(id: string) {
    const format = getIdFormat(id);
    const tag = format.tag || editor.html.defaultTag();
    const doesNeedBlock = format.class || format.id;

    editor.selection.save();
    editor.html.wrap(true, true, true, true, true);
    editor.selection.restore();

    const blocks = editor.selection.blocks();

    // `editor.selection.blocks` returns nested blocks. We need to process only deepest children to prevent
    // multiple style applying for nested blocks. This array keeps the list of original and processed blocks. So
    // if a being processed block contains any block from this list, it is skipped.
    const blocksToCheck = Array.prototype.slice.call(blocks);

    editor.selection.save();
    editor.$el.find('pre').attr('skip', true);

    blocks.forEach((block) => {
      if (editor.node.isList(block)) {
        return;
      }

      if (blocksToCheck.some((blockToCheck: HTMLElement) => blockToCheck !== block && block.contains(blockToCheck))) {
        return;
      }

      let substitute;

      if (block.tagName === 'LI') {
        substitute = substituteLi;
      } else if (block.parentNode.tagName === 'LI') {
        substitute = substituteLiChild;
      } else if (['TD', 'TH'].indexOf(block.parentNode.tagName) !== -1) {
        substitute = substituteTableCellChild;
      } else {
        substitute = substituteOther;
      }

      const $blockNew = substitute($(block), tag, doesNeedBlock);

      if ($blockNew) {
        $blockNew.each((_: any, blockNew: HTMLElement) => {
          // A null value of the attr method argument doesn't remove the attribute in the embedded version of jQuery
          for (const attribute of ['id', 'class']) {
            if (format[attribute]) {
              blockNew.setAttribute(attribute, format[attribute]);
            } else {
              blockNew.removeAttribute(attribute);
            }
          }
          blocksToCheck.push(blockNew);
        });
      }
    });

    editor.$el.find('pre:not([skip="true"]) + pre:not([skip="true"])').each((_: any, element: HTMLElement) => {
      const $element = $(element);
      $element.prev().append(`<br>${$element.html()}`);
      $element.remove();
    });
    editor.$el.find('pre').removeAttr('skip');
    editor.html.unwrap();
    editor.selection.restore();
  }

  function refreshDropdown($dropdown: any) {
    const blocks = editor.selection.blocks();
    const query = getElementFormatIds(blocks[0])
      .map((formatId) => `.fr-command[data-param1="${formatId}"]`)
      .join(', ');

    $dropdown.find(query).addClass('fr-active').attr('aria-selected', true);
  }

  function refreshButton($button: any) {
    if (!editor.opts.paragraphFormatExtendedSelection) {
      return;
    }

    const blocks = editor.selection.blocks();
    const formatIds = getElementFormatIds(blocks[0]);
    const formats = editor.opts.paragraphFormatExtended;
    let title = '\u2014'; // M-dash

    for (let i = 0; i < formats.length; ++i) {
      if (formatIds.indexOf(getFormatId(formats[i])) !== -1) {
        title = editor.language.translate(formats[i].title);
        break;
      }
    }

    $button.find('> span').text(title);
  }

  function getElementFormatIds(element: any) {
    const tagDefault = editor.html.defaultTag();
    let format;

    if (element instanceof HTMLElement) {
      format = {
        tag: element.tagName.toLowerCase(),
        id: element.getAttribute('id'),
        class: element.getAttribute('class'),
      };

      if (['li', 'td', 'th'].indexOf(format.tag) !== -1) {
        format.tag = tagDefault;
      }
    } else {
      format = { tag: tagDefault };
    }

    const formats = [format];

    if (format.tag === tagDefault) {
      formats.push({
        ...format,
        tag: null,
      });
    }

    return formats.map(getFormatId);
  }

  function substituteLi($block: any, tag: any, requireBlock: any) {
    if ((!tag || tag.toLowerCase() === editor.html.defaultTag()) && !requireBlock) {
      return $block;
    }

    let $blockNew;

    if ($block.find('ul, ol').length > 0) {
      $blockNew = $(`<${tag}>`);
      $block.prepend($blockNew);

      for (let child = editor.node.contents($block[0])[0]; child && ['ul', 'ol'].indexOf(child.tagName.toLowerCase()) === -1; ) {
        const next = child.nextSibling;
        $blockNew.append(child);
        child = next;
      }
    } else {
      $blockNew = $(`<${tag}>`).html($block.html());
      $block.html($blockNew);
    }

    return $blockNew;
  }

  function substituteLiChild($block: any, tag: any, requireBlock: any) {
    if (tag.toLowerCase() === editor.html.defaultTag() && !requireBlock) {
      $block.replaceWith($block.html());
      return null;
    }

    return substituteOther($block, tag, requireBlock);
  }

  function substituteTableCellChild($block: any, tag: any, requireBlock: any) {
    if (tag.toLowerCase() === editor.html.defaultTag() && !requireBlock) {
      if (!editor.node.isEmpty($block[0], true)) {
        $block.append('<br/>');
      }

      $block.replaceWith($block.html());
      return null;
    }

    return substituteOther($block, tag, requireBlock);
  }

  function substituteOther($block: any, tag: any, requireBlock: boolean) {
    if (!tag) {
      tag = `div class="fr-temp-div"${editor.node.isEmpty($block[0], true) ? ' data-empty="true"' : ''}`;
    }

    const $blockNew = $(`<${tag} ${editor.node.attributes($block[0])}>`)
      .html($block.html())
      .removeAttr('data-empty');
    $block.replaceWith($blockNew);
    return $blockNew;
  }

  return {
    apply,
    refreshDropdown,
    refreshButton,
  };
};

FroalaEditor.DefineIcon('paragraphFormatExtended', {
  NAME: 'paragraph',
  SVG_KEY: 'paragraphFormat',
});

FroalaEditor.RegisterCommand('paragraphFormatExtended', {
  type: 'dropdown',
  title: 'Paragraph Format',
  plugin: 'paragraphFormatExtended',
  displaySelection(editor: any) {
    return editor.opts.paragraphFormatExtendedSelection;
  },
  defaultSelection(editor: any) {
    return editor.language.translate(editor.opts.paragraphDefaultSelection);
  },
  displaySelectionWidth: 100,
  html() {
    const itemsHTML = this.opts.paragraphFormatExtended
      .map((format) => {
        const title = this.language.translate(format.title);
        const tag = format.tag || this.html.defaultTag();
        const formatId = getFormatId(format);
        // const shortcut = this.shortcuts.get(`paragraphFormatExtended.${formatId}`);

        return (
          `<li role="presentation">` +
          `<${tag}${format.class ? ` class="${format.class}"` : ''}${format.id ? ` id="${format.id}"` : ''} style="padding: 0 !important; margin: 0 !important;" role="presentation">` +
          `<a class="fr-command" tabIndex="-1" role="option" data-cmd="paragraphFormatExtended" data-param1="${formatId}" title="${title}">` +
          title +
          // (shortcut ? `<span class="fr-shortcut">{shortcut}</span>` : '') +
          `</a>` +
          `</${tag}>` +
          `</li>`
        );
      })
      .join('\n');

    return `<ul class="fr-dropdown-list" role="presentation">${itemsHTML}</ul>`;
  },
  callback(command: any, value: any) {
    this.paragraphFormatExtended.apply(value);
  },
  refresh($button: any) {
    this.paragraphFormatExtended.refreshButton($button);
  },
  refreshOnShow($button: any, $dropdown: any) {
    this.paragraphFormatExtended.refreshDropdown($dropdown);
  },
});

function getFormatId(format: any) {
  let str = '';

  if (format.tag) {
    str += format.tag.toLowerCase();
  }

  if (format.id) {
    str += `#${format.id}`;
  }

  if (format.class) {
    str += (format.class instanceof Array ? format.class : String(format.class).split(/\s+/))
      .filter((part) => part)
      .sort()
      .map((part) => `.${part}`)
      .join('');
  }

  return str;
}

function getIdFormat(id: string) {
  const parts = /([^.#]*)(#[^.]+|.{0})(\.[\s\S]+|.{0})/.exec(id);

  if (parts) {
    return {
      tag: parts[1].toLowerCase() || null,
      id: parts[2].slice(1) || null,
      class:
        parts[3]
          .split('.')
          .filter((part) => part)
          .join(' ') || null,
    };
  } else {
    return {};
  }
}
